import axios from "@/plugins/axios";
import utils from "@/stores/utils";

import campaigncontents from "./submodules/campaigncontents";
import campaignrecipients from "./submodules/campaignrecipients";

const { getStorageKey } = utils;

const state = {
    campaign: null,
    campaign_loading: false,
    section: null,

    stats: null,
    stats_loading: false,

    validation: null,
    validation_loading: false,

    distribution: [],
    logs: [],
    logs_loading: false,

    sending: false,
};

const mutations = {
    updateCampaign(state, campaign) {
        state.campaign = campaign;
    },
    updateCampaignLoading(state, loading) {
        state.campaign_loading = loading;
    },
    updateSection(state, section) {
        state.section = section;
    },
    updateStats(state, stats) {
        state.stats = stats;
    },
    updateStatsLoading(state, loading) {
        state.stats_loading = loading;
    },
    updateValidation(state, validation) {
        state.validation = validation;
    },
    updateValidationLoading(state, loading) {
        state.validation_loading = loading;
    },
    updateDistribution(state, distribution) {
        state.distribution = distribution;
    },
    updateLogs(state, logs) {
        state.logs = logs;
    },
    updateLogsLoading(state, loading) {
        state.logs_loading = loading;
    },
    updateSending(state, sending) {
        state.sending = sending;
    },
    decrementPendingCount(state, count) {
        const n = Number(count) || 0;
        if (state.stats) {
            state.stats = {
                ...state.stats,
                pending_count: Math.max(0, (state.stats.pending_count || 0) - n),
            };
        }
        if (state.campaign) {
            state.campaign = {
                ...state.campaign,
                pending_count: Math.max(0, (state.campaign.pending_count || 0) - n),
            };
        }
    },
};

const actions = {
    async fetchCampaign({ commit, state }) {
        commit("updateCampaignLoading", true);
        try {
            const response = await axios.get(`/api/campaigns/${state.campaign.id}/`);
            commit("updateCampaign", response.data);
            commit("updateStats", response.data.stats);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleResourceError(xhr_error);
        } finally {
            commit("updateCampaignLoading", false);
        }
    },

    async fetchStats({ commit, state }) {
        commit("updateStatsLoading", true);
        try {
            const response = await axios.get(`/api/campaigns/${state.campaign.id}/stats/`);
            commit("updateStats", response.data);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateStatsLoading", false);
        }
    },

    async fetchValidation({ commit, state }) {
        commit("updateValidationLoading", true);
        try {
            const response = await axios.get(
                `/api/campaigns/${state.campaign.id}/validation/`
            );
            commit("updateValidation", response.data);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateValidationLoading", false);
        }
    },

    /** How the pending recipients split across the messages. */
    async fetchDistribution({ commit, state }) {
        try {
            const response = await axios.get(
                `/api/campaigns/${state.campaign.id}/distribution/`
            );
            commit("updateDistribution", response.data);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async fetchLogs({ commit, state }) {
        commit("updateLogsLoading", true);
        try {
            const response = await axios.get(`/api/campaigns/${state.campaign.id}/logs/`);
            commit("updateLogs", response.data);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateLogsLoading", false);
        }
    },

    async changeStatus({ commit, dispatch, state }, params) {
        try {
            const response = await axios.post(
                `/api/campaigns/${state.campaign.id}/status/`,
                { status: params.status }
            );
            commit("updateCampaign", response.data);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    /**
     * Queue every pending recipient (or a subset).
     *
     * Sending is asynchronous: the response only says how many emails were
     * queued, and the statuses land through the Resend webhooks afterwards.
     */
    async send({ commit, dispatch, state }, params) {
        commit("updateSending", true);
        try {
            const response = await axios.post(`/api/campaigns/${state.campaign.id}/send/`, {
                recipients: params?.recipients,
            });
            const queuedIds = response.data.recipients || [];
            commit("recipients/markRecipientsQueued", queuedIds);
            commit("decrementPendingCount", queuedIds.length);
            await dispatch("fetchCampaign");
            await dispatch("recipients/fetchRecipients");
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateSending", false);
        }
    },

    async saveCampaign({ commit, dispatch, state }, params) {
        try {
            const response = await axios.patch(
                `/api/campaigns/${state.campaign.id}/`,
                params.campaign
            );
            commit("updateCampaign", response.data);
            return response.data;
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        }
    },

    async init({ commit, dispatch }, params) {
        commit("updateCampaign", params.campaign);
        commit("updateSection", params.section || "contents");
        commit("updateStats", params.campaign.stats || null);
        commit("updateValidation", null);
        commit("updateLogs", []);

        await dispatch("contents/init", {
            url: `/api/campaigns/${params.campaign.id}/contents/`,
        });
        await dispatch("recipients/init", {
            url: `/api/campaigns/${params.campaign.id}/recipients/`,
        });

        dispatch("contents/fetchContents");
        dispatch("recipients/fetchRecipients");
        dispatch("fetchValidation");
    },
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    modules: {
        contents: campaigncontents({
            fieldsToSave: ["limit"],
            storageKey: getStorageKey("campaign", "contents"),
        }),
        recipients: campaignrecipients({
            fieldsToSave: ["limit", "ordering"],
            storageKey: getStorageKey("campaign", "recipients"),
        }),
    },
};
